import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { getUserToken } from '@/redux/user';
import { ENV } from '@/utils/env';
import { downloadFile } from '@/utils/file/utils';

const NO_TICKETS_ERROR_CODE = 'no_tickets_for_date_range';

type StartExportOptions = {
    startDate?: string;
    endDate?: string;
};

export const useTicketExport = (customerId: number) => {
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const [isExporting, setIsExporting] = useState(false);

    const startExport = useCallback(
        async (options?: StartExportOptions) => {
            if (isExporting) return;

            setIsExporting(true);
            toast.info(intl.formatMessage({ id: 'customer.ordersList.exportStarted' }));

            const params = new URLSearchParams({ customerId: String(customerId) });
            if (options?.startDate && options?.endDate) {
                params.append('startDate', options.startDate);
                params.append('endDate', options.endDate);
            }

            try {
                const response = await fetch(`${ENV.API_URL}/orders/tickets/export?${params.toString()}`, {
                    method: 'POST',
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    const body = await response.json().catch(() => null);
                    if (body?.code === NO_TICKETS_ERROR_CODE) {
                        toast.info(intl.formatMessage({ id: 'customer.ordersList.exportNoTicketsForDates' }));
                        return;
                    }
                    throw new Error(`Export failed with status ${response.status}`);
                }

                const blob = await response.blob();
                const arrayBuffer = await blob.arrayBuffer();
                downloadFile(`tickets-${customerId}.pdf`, arrayBuffer, 'application/pdf');

                toast.success(intl.formatMessage({ id: 'customer.ordersList.exportSuccess' }));
            } catch {
                toast.error(intl.formatMessage({ id: 'customer.ordersList.exportFailed' }));
            } finally {
                setIsExporting(false);
            }
        },
        [customerId, token, intl, isExporting],
    );

    return { startExport, isExporting };
};
